import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface PosAgentInfo {
  terminalId: string;
  storeId: string;
  chainId: string;
  screenWidth: number;
  screenHeight: number;
  connectedAt: string;
}

export interface DesktopFrame {
  controlId: string;
  jpeg: string; // base64
  width: number;
  height: number;
  seq: number;
}

@Injectable({ providedIn: 'root' })
export class PosAgentAdminService {
  private onlineAgents$ = new BehaviorSubject<Map<string, PosAgentInfo>>(new Map());

  activeControlId: string | null = null;
  activeTerminalInfo: PosAgentInfo | null = null;

  desktopFrame$ = new Subject<DesktopFrame>();
  controlEnded$ = new Subject<string>(); // emits controlId

  private listenersRegistered = false;

  constructor() {}

  /**
   * Registers all pos-agent:* listeners on the provided socket.
   * Called once from RemoteControlAdminService.connect() after socket creation.
   * Guard prevents double-registration if somehow called again on the same socket.
   */
  registerSocketListeners(socket: any): void {
    if (this.listenersRegistered) return;
    this.listenersRegistered = true;

    socket.on('pos-agent:online', (data: PosAgentInfo) => {
      const map = new Map(this.onlineAgents$.value);
      map.set(data.terminalId, data);
      this.onlineAgents$.next(map);
    });

    socket.on('pos-agent:offline', (data: { terminalId: string; chainId: string }) => {
      const map = new Map(this.onlineAgents$.value);
      map.delete(data.terminalId);
      this.onlineAgents$.next(map);
      if (this.activeTerminalInfo?.terminalId === data.terminalId) {
        this.controlEnded$.next(this.activeControlId ?? '');
        this.activeControlId = null;
        this.activeTerminalInfo = null;
      }
    });

    socket.on('pos-agent:frame', (data: DesktopFrame) => {
      this.desktopFrame$.next(data);
    });

    socket.on('pos-agent:control-ended', (data: { controlId: string }) => {
      this.controlEnded$.next(data.controlId);
      if (this.activeControlId === data.controlId) {
        this.activeControlId = null;
        this.activeTerminalInfo = null;
      }
    });
  }

  /**
   * Resets the registration guard. Call this only if the socket itself is replaced
   * (e.g. full disconnect + reconnect cycle in RemoteControlAdminService).
   */
  resetListenerGuard(): void {
    this.listenersRegistered = false;
  }

  getOnlineAgents$() {
    return this.onlineAgents$.asObservable();
  }

  isAgentOnline(terminalId: string): boolean {
    return this.onlineAgents$.value.has(terminalId);
  }

  getAgentInfo(terminalId: string): PosAgentInfo | undefined {
    return this.onlineAgents$.value.get(terminalId);
  }

  requestDesktopControl(socket: any, terminalId: string): string {
    const controlId = 'pagent-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    this.activeControlId = controlId;
    this.activeTerminalInfo = this.onlineAgents$.value.get(terminalId) ?? null;
    socket.emit('pos-agent:control-request', { controlId, terminalId });
    return controlId;
  }

  sendDesktopInput(
    socket: any,
    controlId: string,
    event: MouseEvent | WheelEvent | KeyboardEvent,
    imgEl: HTMLImageElement,
    agentScreenWidth: number,
    agentScreenHeight: number,
  ): void {
    if (!controlId || !socket) return;

    const { normX, normY } = this.getNormalizedCoords(event, imgEl, agentScreenWidth, agentScreenHeight);

    if (event instanceof WheelEvent) {
      socket.emit('pos-agent:input', {
        controlId,
        type: 'wheel',
        normX,
        normY,
        deltaY: event.deltaY,
      });
    } else if (event instanceof KeyboardEvent) {
      socket.emit('pos-agent:input', {
        controlId,
        type: event.type,
        normX: 0,
        normY: 0,
        key: event.key,
        code: event.code,
        modifiers: {
          shiftKey: event.shiftKey,
          ctrlKey: event.ctrlKey,
          altKey: event.altKey,
          metaKey: event.metaKey,
        },
      });
    } else {
      const me = event as MouseEvent;
      const isClickEvent = me.type === 'mousedown' || me.type === 'mouseup';
      const payload: any = {
        controlId,
        type: me.type,
        normX,
        normY,
      };
      if (isClickEvent) {
        payload.button = me.button; // integer: 0=left, 1=middle, 2=right — matches C# int?
      }
      socket.emit('pos-agent:input', payload);
    }
  }

  endDesktopControl(socket: any, controlId: string): void {
    if (socket) {
      socket.emit('pos-agent:control-end', { controlId });
    }
    if (this.activeControlId === controlId) {
      this.activeControlId = null;
      this.activeTerminalInfo = null;
    }
  }

  /**
   * Letterbox-aware coordinate normalization for <img> with object-fit: contain.
   * Uses the agent's actual screen resolution to compute content area inside the element.
   */
  private getNormalizedCoords(
    event: MouseEvent | WheelEvent | KeyboardEvent,
    imgEl: HTMLImageElement,
    agentScreenWidth: number,
    agentScreenHeight: number,
  ): { normX: number; normY: number } {
    if (event instanceof KeyboardEvent) return { normX: 0, normY: 0 };

    const me = event as MouseEvent;
    const rect = imgEl.getBoundingClientRect();
    const mouseX = me.clientX - rect.left;
    const mouseY = me.clientY - rect.top;

    if (agentScreenWidth > 0 && agentScreenHeight > 0) {
      const containerAspect = rect.width / rect.height;
      const contentAspect = agentScreenWidth / agentScreenHeight;
      let contentW: number, contentH: number, offsetX: number, offsetY: number;

      if (contentAspect > containerAspect) {
        // Wider than container — letterbox (black bars top/bottom)
        contentW = rect.width;
        contentH = rect.width / contentAspect;
        offsetX = 0;
        offsetY = (rect.height - contentH) / 2;
      } else {
        // Taller than container — pillarbox (black bars left/right)
        contentH = rect.height;
        contentW = rect.height * contentAspect;
        offsetX = (rect.width - contentW) / 2;
        offsetY = 0;
      }

      const relX = mouseX - offsetX;
      const relY = mouseY - offsetY;
      return {
        normX: Math.max(0, Math.min(1, relX / contentW)),
        normY: Math.max(0, Math.min(1, relY / contentH)),
      };
    }

    // Fallback: simple normalization
    return {
      normX: Math.max(0, Math.min(1, mouseX / rect.width)),
      normY: Math.max(0, Math.min(1, mouseY / rect.height)),
    };
  }
}
